export type RoleName = 'Admin' | 'Employee' | 'Finance' | 'HR' | 'PM' | 'Sales';

/**
 * Areas of the app a signed-in user may read. Write authority stays with the
 * per-route role guards; this governs visibility only.
 *
 * Employee is the floor: a user carrying no other role sees their own work and
 * the People screens they are a subject of, and nothing else. Every other role
 * adds the areas it needs on top, so a person holding several roles gets the
 * union of them.
 */
export type ReadArea =
  | 'self'
  | 'people'
  | 'people_rates'
  | 'people_kpi'
  | 'projects'
  | 'renewals'
  | 'crm'
  | 'sales'
  | 'ledger'
  | 'billing'
  | 'payroll'
  | 'vault'
  | 'analytics'
  | 'ideas'
  | 'cost_centers'
  | 'setup'
  | 'admin';

const FLOOR_AREAS: readonly ReadArea[] = ['self', 'people', 'projects'];
const EMPLOYEE_AREAS: readonly ReadArea[] = [...FLOOR_AREAS, 'ideas'];

// Cost centers sit under Setup, but a PM must read them to create a project
// (every project maps to one), so the list is its own area rather than `setup`.
// PM is deliberately narrower than Employee on ideas. Everyone on staff carries
// Employee as a base role, so Employee's own ideas grant is suppressed while PM
// is held (see areasFor); a project manager reads ideas only through a role
// that grants it on its own merits (Sales, Finance, Admin).
const AREAS_BY_ROLE: Record<RoleName, readonly ReadArea[]> = {
  Employee: EMPLOYEE_AREAS,
  HR: EMPLOYEE_AREAS,
  PM: [...FLOOR_AREAS, 'renewals', 'crm', 'cost_centers'],
  Sales: [...EMPLOYEE_AREAS, 'crm', 'sales', 'analytics'],
  Finance: [
    ...EMPLOYEE_AREAS,
    'renewals',
    'people_rates',
    'crm',
    'sales',
    'ledger',
    'billing',
    'payroll',
    'vault',
    'analytics',
    'cost_centers',
    'setup',
  ],
  Admin: [
    ...EMPLOYEE_AREAS,
    'renewals',
    'people_rates',
    'crm',
    'sales',
    'ledger',
    'billing',
    'payroll',
    'vault',
    'analytics',
    'cost_centers',
    'setup',
    'admin',
    'people_kpi',
  ],
};

/**
 * A user with no role rows still gets the Employee floor rather than a blank
 * app — the alternative is a signed-in account that cannot reach its own tasks.
 */
function effectiveRoles(roles: readonly RoleName[]): readonly RoleName[] {
  return roles.length === 0 ? ['Employee'] : roles;
}

function areasFor(role: RoleName, roles: readonly RoleName[]): readonly ReadArea[] {
  if (role === 'Employee' && roles.includes('PM')) return FLOOR_AREAS;
  return AREAS_BY_ROLE[role] ?? [];
}

export function readableAreas(roles: readonly RoleName[]): ReadArea[] {
  const areas = new Set<ReadArea>(FLOOR_AREAS);
  const held = effectiveRoles(roles);
  for (const role of held) {
    for (const area of areasFor(role, held)) areas.add(area);
  }
  return [...areas];
}

export function canRead(roles: readonly RoleName[], area: ReadArea): boolean {
  if (FLOOR_AREAS.includes(area)) return true;
  const held = effectiveRoles(roles);
  return held.some((role) => areasFor(role, held).includes(area));
}

/**
 * Whether the user may look at people other than themselves. Attendance and
 * leave are company-wide for the roles that administer them and self-only for
 * everyone else, so the read is filtered rather than refused.
 */
export function canReadAllPeople(roles: readonly RoleName[]): boolean {
  return roles.some(
    (role) => role === 'Admin' || role === 'Finance' || role === 'HR' || role === 'PM',
  );
}
